import Button from '@bugbug/core/components/Button';
import { H3 } from '@bugbug/core/theme/typography';
import * as T from '@bugbug/core/utils/toolbox';
import { useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';

import type { MouseEventHandler } from 'react';

import type { PaidFeatureName } from '@bugbug/core/types/paidFeatures';
import type { SubscriptionPlanType } from '@bugbug/core/types/subscriptions';
import type { SideEffect } from '@bugbug/core/types/utils';
import { SUBSCRIPTION_TYPE_TIERS } from '~/constants/subscription';
import useAppRoutes from '~/hooks/useAppRoutes';
import {
  selectCurrentOrganizationId,
  selectSubscription,
} from '~/modules/organization/organization.selectors';
import { useAppSelector } from '~/modules/store';
import { useGetSubscriptionsPlansQuery } from '~/modules/subscriptions/subscriptions.api';
import analytics, { TRACK_EVENT_ARG_TYPE } from '~/services/analytics';

import * as S from './PaidFeatureGuard.styled';

interface PaidFeatureBannerProps {
  className?: string;
  fullInfo?: boolean;
  messageFor?: string;
  onRedirect?: SideEffect;
  onUpgradeClick?: MouseEventHandler;
}

export const PaidFeatureBanner = ({
  className,
  fullInfo = false,
  messageFor,
  onUpgradeClick,
  onRedirect = T.noop,
}: PaidFeatureBannerProps) => {
  const { t } = useTranslation();

  const organizationId = useAppSelector(selectCurrentOrganizationId);

  const featureLabels: Record<PaidFeatureName, string> = {
    alerts: t('paidFeatureBanner.messageIntro.alerts', 'Alerts are'),
    execute: t('paidFeatureBanner.messageIntro.execute', 'Custom JavaScript steps are'),
    integrations: t('paidFeatureBanner.messageIntro.integrations', 'Integrations are'),
    'run test via cli': t('paidFeatureBanner.messageIntro.runTestViaCli', 'Run Test via CLI is'),
    'run suite via cli': t('paidFeatureBanner.messageIntro.runSuiteViaCli', 'Run Suite via CLI is'),
    schedules: t('paidFeatureBanner.messageIntro.schedules', 'Schedules are'),
    setLocalVariable: t(
      'paidFeatureBanner.messageIntro.setLocalVariable',
      'Set Variable steps are',
    ),
    variables: t('paidFeatureBanner.messageIntro.variables', 'Variables are'),
    api: t('paidFeatureBanner.messageIntro.api', 'API is'),
  };

  const featurePlans: Record<PaidFeatureName, SubscriptionPlanType> = {
    alerts: 'pro',
    execute: 'pro',
    integrations: 'pro',
    'run test via cli': 'pro',
    'run suite via cli': 'pro',
    schedules: 'pro',
    setLocalVariable: 'pro',
    variables: 'pro',
    api: 'business',
  };

  const { data: subscriptionsPlans = [] } = useGetSubscriptionsPlansQuery();
  const activeSubscription = useAppSelector(selectSubscription);
  const subscriptionPlanPrice = subscriptionsPlans.find(
    (p) => p.type === featurePlans[messageFor as PaidFeatureName] && p.billingCycle === 'yearly',
  )?.pricePerMonth;

  const isPaidFeature = messageFor && messageFor in featureLabels;

  const { push } = useAppRoutes('subscription');

  const handleOnClick = useCallback<MouseEventHandler>(
    (event) => {
      if (isPaidFeature) {
        analytics.trackEvent('upgrade_button_clicked', {
          [TRACK_EVENT_ARG_TYPE.ORGANIZATION_ID]: organizationId,
          [TRACK_EVENT_ARG_TYPE.CONTEXT]: messageFor,
        });
      }

      if (onUpgradeClick) {
        onUpgradeClick(event);
      } else {
        onRedirect();
        push('subscription', { organizationId });
      }
    },
    [isPaidFeature, messageFor, onRedirect, onUpgradeClick, organizationId, push],
  );

  const feature = featureLabels[messageFor as PaidFeatureName];
  const planType = featurePlans[messageFor as PaidFeatureName];
  const planName = planType?.toUpperCase();
  const planPrice = subscriptionPlanPrice?.toString();
  const isMoreExpensivePlan =
    SUBSCRIPTION_TYPE_TIERS.indexOf(planType) > SUBSCRIPTION_TYPE_TIERS.indexOf('pro');

  const isLowerPlan =
    !activeSubscription ||
    SUBSCRIPTION_TYPE_TIERS.indexOf(activeSubscription.type) <
      SUBSCRIPTION_TYPE_TIERS.indexOf(planType);

  const planInfo = isMoreExpensivePlan
    ? t('paidFeatureBanner.planInfo.moreExpensive', 'the {{planName}} plan', {
        planName,
      })
    : t('paidFeatureBanner.planInfo.paidPlans', 'paid plans');

  return isLowerPlan && isPaidFeature && planPrice ? (
    <S.Container fullInfo={fullInfo} className={className}>
      {fullInfo && <S.MoneyIcon />}

      <S.TextContainer>
        <H3>{t('paidFeatureBanner.header.label', 'Upgrade to use this feature')}</H3>

        <S.RegularParagraph>
          <Trans
            i18nKey="paidFeatureBanner.mainContent"
            values={{
              feature,
              planInfo,
            }}
          >
            {{ feature }} available only in {{ planInfo }}. Upgrade your subscription to unlock this
            feature.
          </Trans>
        </S.RegularParagraph>
      </S.TextContainer>

      <S.ButtonContainer>
        <Button iconName="upgrade" onClick={handleOnClick} variant="primary">
          {t('paidFeatureBanner.unlockButtonLabel', 'Unlock this feature')}
        </Button>
      </S.ButtonContainer>
    </S.Container>
  ) : null;
};
